/**
 * Registry Digitization Workflow Service
 *
 * Manages the digitization of physical personnel files including
 * barcode/QR generation, batch scanning, quality assurance, and progress tracking.
 */

export interface DigitizationProject {
  id: string;
  name: string;
  description: string;
  status: ProjectStatus;
  priority: ProjectPriority;
  startDate: Date;
  targetEndDate: Date;
  actualEndDate?: Date;
  createdBy: string;
  assignedTeam: string[];
  mdaScope: string[];
  totalFiles: number;
  completedFiles: number;
  inProgressFiles: number;
  pendingFiles: number;
  failedFiles: number;
  qualityScore: number;
  batches: DigitizationBatch[];
}

export type ProjectStatus =
  | 'planning'
  | 'in_progress'
  | 'paused'
  | 'completed'
  | 'cancelled';

export type ProjectPriority =
  | 'critical'    // Active executives, VIPs
  | 'high'        // Frequently accessed files
  | 'normal'      // Standard files
  | 'low';        // Archived/inactive files

export interface DigitizationBatch {
  id: string;
  projectId: string;
  batchNumber: number;
  status: BatchStatus;
  assignedTo: string;
  startedAt?: Date;
  completedAt?: Date;
  files: DigitizationFile[];
  qualityCheckStatus: QualityStatus;
  qualityCheckedBy?: string;
  qualityCheckedAt?: Date;
  qualityNotes?: string;
}

export type BatchStatus =
  | 'pending'
  | 'scanning'
  | 'processing'
  | 'quality_check'
  | 'approved'
  | 'rejected'
  | 'completed';

export type QualityStatus =
  | 'pending'
  | 'passed'
  | 'failed'
  | 'needs_review';

export interface DigitizationFile {
  id: string;
  pfileId: string;
  fileNumber: string;
  staffName: string;
  batchId: string;
  status: FileDigitizationStatus;
  barcode: string;
  qrCode: string;
  physicalLocation: string;
  documentCount: number;
  scannedPages: number;
  expectedPages: number;
  scanStartedAt?: Date;
  scanCompletedAt?: Date;
  processedAt?: Date;
  qualityScore: number;
  issues: DigitizationIssue[];
  metadata: FileMetadata;
}

export type FileDigitizationStatus =
  | 'pending'
  | 'retrieved'      // Physical file retrieved
  | 'scanning'       // Currently being scanned
  | 'processing'     // OCR/indexing in progress
  | 'quality_check'  // Awaiting QA
  | 'approved'       // Passed QA
  | 'rejected'       // Failed QA, needs rescan
  | 'completed'      // Fully digitized
  | 'failed';        // Could not be digitized

export interface DigitizationIssue {
  id: string;
  type: IssueType;
  severity: 'critical' | 'major' | 'minor';
  description: string;
  pageNumber?: number;
  resolution?: string;
  resolvedAt?: Date;
  resolvedBy?: string;
}

export type IssueType =
  | 'missing_pages'
  | 'illegible_text'
  | 'damaged_document'
  | 'wrong_orientation'
  | 'poor_quality'
  | 'missing_metadata'
  | 'duplicate_scan'
  | 'incomplete_file';

export interface FileMetadata {
  staffId?: string;
  dateOfBirth?: string;
  appointmentDate?: string;
  currentGrade?: string;
  department?: string;
  documentTypes: string[];
  dateRange: { start?: string; end?: string };
  keywords: string[];
}

export interface ScanStation {
  id: string;
  name: string;
  location: string;
  status: 'online' | 'offline' | 'busy' | 'maintenance';
  type: 'flatbed' | 'adf' | 'overhead' | 'mobile';
  capabilities: {
    dpi: number[];
    colorModes: ('color' | 'grayscale' | 'bw')[];
    paperSizes: string[];
    duplexSupport: boolean;
    adfCapacity?: number;
  };
  currentOperator?: string;
  currentBatch?: string;
  pagesScannedToday: number;
  lastActivity?: Date;
}

export interface DigitizationStats {
  projectId: string;
  totalFiles: number;
  completedFiles: number;
  completionPercentage: number;
  averageQualityScore: number;
  averagePagesPerFile: number;
  averageScanTime: number; // minutes
  issuesByType: Record<IssueType, number>;
  dailyProgress: { date: string; completed: number }[];
  projectedCompletion: Date;
  scannerUtilization: number;
}

// Barcode/QR code generation utilities
function generateBarcode(pfileId: string, fileNumber: string): string {
  // Generate a unique barcode combining identifiers
  const timestamp = Date.now().toString(36);
  const hash = simpleHash(`${pfileId}-${fileNumber}`);
  return `OHCS-${fileNumber.replace(/[^A-Z0-9]/gi, '')}-${hash}-${timestamp}`.toUpperCase();
}

function generateQRCode(pfileId: string, fileNumber: string, staffName: string): string {
  // Generate QR code data (JSON encoded)
  const qrData = {
    system: 'OHCS-RMS',
    version: '1.0',
    pfileId,
    fileNumber,
    staffName,
    generated: new Date().toISOString(),
    checksum: simpleHash(`${pfileId}-${fileNumber}-${staffName}`),
  };
  return JSON.stringify(qrData);
}

function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36).toUpperCase().slice(0, 6);
}

class DigitizationWorkflowService {
  /**
   * Create a new digitization project
   */
  createProject(params: {
    name: string;
    description: string;
    priority: ProjectPriority;
    targetEndDate: Date;
    createdBy: string;
    mdaScope: string[];
    estimatedFiles: number;
  }): DigitizationProject {
    return {
      id: `proj_${Date.now()}`,
      name: params.name,
      description: params.description,
      status: 'planning',
      priority: params.priority,
      startDate: new Date(),
      targetEndDate: params.targetEndDate,
      createdBy: params.createdBy,
      assignedTeam: [],
      mdaScope: params.mdaScope,
      totalFiles: params.estimatedFiles,
      completedFiles: 0,
      inProgressFiles: 0,
      pendingFiles: params.estimatedFiles,
      failedFiles: 0,
      qualityScore: 0,
      batches: [],
    };
  }

  /**
   * Create a batch of files for digitization
   */
  createBatch(params: {
    projectId: string;
    batchNumber: number;
    assignedTo: string;
    files: Array<{
      pfileId: string;
      fileNumber: string;
      staffName: string;
      physicalLocation: string;
      expectedPages: number;
    }>;
  }): DigitizationBatch {
    const batchId = `batch_${params.projectId}_${params.batchNumber}`;

    const files: DigitizationFile[] = params.files.map(f => ({
      id: `file_${f.pfileId}_${Date.now()}`,
      pfileId: f.pfileId,
      fileNumber: f.fileNumber,
      staffName: f.staffName,
      batchId,
      status: 'pending',
      barcode: generateBarcode(f.pfileId, f.fileNumber),
      qrCode: generateQRCode(f.pfileId, f.fileNumber, f.staffName),
      physicalLocation: f.physicalLocation,
      documentCount: 0,
      scannedPages: 0,
      expectedPages: f.expectedPages,
      qualityScore: 0,
      issues: [],
      metadata: {
        documentTypes: [],
        dateRange: {},
        keywords: [],
      },
    }));

    return {
      id: batchId,
      projectId: params.projectId,
      batchNumber: params.batchNumber,
      status: 'pending',
      assignedTo: params.assignedTo,
      files,
      qualityCheckStatus: 'pending',
    };
  }

  /**
   * Generate barcode label for a file
   */
  generateBarcodeLabel(file: DigitizationFile): {
    barcode: string;
    qrCode: string;
    label: string;
  } {
    return {
      barcode: file.barcode,
      qrCode: file.qrCode,
      label: `
┌─────────────────────────────────────┐
│ OHCS RECORDS MANAGEMENT SYSTEM      │
├─────────────────────────────────────┤
│ File: ${file.fileNumber.padEnd(28)}│
│ Staff: ${file.staffName.slice(0, 27).padEnd(27)}│
│ Location: ${file.physicalLocation.slice(0, 24).padEnd(24)}│
├─────────────────────────────────────┤
│ ||||| ${file.barcode} |||||         │
│ [QR]                                │
└─────────────────────────────────────┘
      `.trim(),
    };
  }

  /**
   * Update file digitization status
   */
  updateFileStatus(
    file: DigitizationFile,
    status: FileDigitizationStatus,
    updates?: Partial<DigitizationFile>
  ): DigitizationFile {
    const now = new Date();

    const statusTimestamps: Partial<DigitizationFile> = {};
    if (status === 'scanning' && !file.scanStartedAt) {
      statusTimestamps.scanStartedAt = now;
    }
    if (status === 'processing' && !file.scanCompletedAt) {
      statusTimestamps.scanCompletedAt = now;
    }
    if (status === 'completed' || status === 'approved') {
      statusTimestamps.processedAt = now;
    }

    return {
      ...file,
      ...updates,
      ...statusTimestamps,
      status,
    };
  }

  /**
   * Add quality issue to file
   */
  addQualityIssue(
    file: DigitizationFile,
    issue: Omit<DigitizationIssue, 'id'>
  ): DigitizationFile {
    const newIssue: DigitizationIssue = {
      ...issue,
      id: `issue_${Date.now()}`,
    };

    return {
      ...file,
      issues: [...file.issues, newIssue],
      qualityScore: this.calculateQualityScore(file.issues.length + 1, file.scannedPages),
    };
  }

  /**
   * Calculate quality score based on issues
   */
  private calculateQualityScore(issueCount: number, pageCount: number): number {
    if (pageCount === 0) return 0;
    const issueRate = issueCount / pageCount;
    const score = Math.max(0, 100 - (issueRate * 100 * 5)); // 5% penalty per issue per page
    return Math.round(score);
  }

  /**
   * Calculate project statistics
   */
  calculateProjectStats(project: DigitizationProject): DigitizationStats {
    const allFiles = project.batches.flatMap(b => b.files);
    const completedFiles = allFiles.filter(f => f.status === 'completed');

    const totalPages = completedFiles.reduce((sum, f) => sum + f.scannedPages, 0);
    const totalScanTime = completedFiles.reduce((sum, f) => {
      if (f.scanStartedAt && f.scanCompletedAt) {
        return sum + (new Date(f.scanCompletedAt).getTime() - new Date(f.scanStartedAt).getTime()) / 60000;
      }
      return sum;
    }, 0);

    const issuesByType: Record<IssueType, number> = {
      missing_pages: 0,
      illegible_text: 0,
      damaged_document: 0,
      wrong_orientation: 0,
      poor_quality: 0,
      missing_metadata: 0,
      duplicate_scan: 0,
      incomplete_file: 0,
    };

    allFiles.forEach(f => {
      f.issues.forEach(issue => {
        issuesByType[issue.type]++;
      });
    });

    // Calculate daily progress (last 30 days)
    const dailyProgress: { date: string; completed: number }[] = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];

      const completedOnDay = completedFiles.filter(f => {
        if (!f.processedAt) return false;
        return new Date(f.processedAt).toISOString().split('T')[0] === dateStr;
      }).length;

      dailyProgress.push({ date: dateStr, completed: completedOnDay });
    }

    // Project completion estimate
    const avgDailyRate = completedFiles.length / 30; // Simplified
    const remainingFiles = project.totalFiles - completedFiles.length;
    const daysRemaining = avgDailyRate > 0 ? Math.ceil(remainingFiles / avgDailyRate) : 365;
    const projectedCompletion = new Date();
    projectedCompletion.setDate(projectedCompletion.getDate() + daysRemaining);

    return {
      projectId: project.id,
      totalFiles: project.totalFiles,
      completedFiles: completedFiles.length,
      completionPercentage: Math.round((completedFiles.length / project.totalFiles) * 100),
      averageQualityScore: completedFiles.length > 0
        ? Math.round(completedFiles.reduce((sum, f) => sum + f.qualityScore, 0) / completedFiles.length)
        : 0,
      averagePagesPerFile: completedFiles.length > 0
        ? Math.round(totalPages / completedFiles.length)
        : 0,
      averageScanTime: completedFiles.length > 0
        ? Math.round(totalScanTime / completedFiles.length)
        : 0,
      issuesByType,
      dailyProgress,
      projectedCompletion,
      scannerUtilization: 75, // Would be calculated from scanner data
    };
  }

  /**
   * Prioritize files for digitization based on access frequency
   */
  prioritizeFiles(files: Array<{
    pfileId: string;
    fileNumber: string;
    staffName: string;
    accessCount: number;
    lastAccessed?: Date;
    staffStatus: 'active' | 'inactive' | 'retired';
    grade?: number;
  }>): Array<{ pfileId: string; priority: number; reason: string }> {
    return files.map(f => {
      let priority = 0;
      const reasons: string[] = [];

      // Active staff get higher priority
      if (f.staffStatus === 'active') {
        priority += 50;
        reasons.push('Active staff');
      }

      // Higher grades get priority
      if (f.grade && f.grade >= 14) {
        priority += 30;
        reasons.push('Senior officer (Grade 14+)');
      }

      // Frequently accessed files
      if (f.accessCount > 10) {
        priority += 20;
        reasons.push('Frequently accessed');
      }

      // Recently accessed
      if (f.lastAccessed) {
        const daysSinceAccess = (Date.now() - new Date(f.lastAccessed).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceAccess < 30) {
          priority += 15;
          reasons.push('Recently accessed');
        }
      }

      return {
        pfileId: f.pfileId,
        priority,
        reason: reasons.join(', ') || 'Standard priority',
      };
    }).sort((a, b) => b.priority - a.priority);
  }

  /**
   * Get status label
   */
  getFileStatusLabel(status: FileDigitizationStatus): string {
    const labels: Record<FileDigitizationStatus, string> = {
      pending: 'Pending',
      retrieved: 'Retrieved',
      scanning: 'Scanning',
      processing: 'Processing',
      quality_check: 'Quality Check',
      approved: 'Approved',
      rejected: 'Needs Rescan',
      completed: 'Completed',
      failed: 'Failed',
    };
    return labels[status];
  }

  /**
   * Get issue type label
   */
  getIssueTypeLabel(type: IssueType): string {
    const labels: Record<IssueType, string> = {
      missing_pages: 'Missing Pages',
      illegible_text: 'Illegible Text',
      damaged_document: 'Damaged Document',
      wrong_orientation: 'Wrong Orientation',
      poor_quality: 'Poor Scan Quality',
      missing_metadata: 'Missing Metadata',
      duplicate_scan: 'Duplicate Scan',
      incomplete_file: 'Incomplete File',
    };
    return labels[type];
  }
}

// Export singleton instance
export const digitizationWorkflow = new DigitizationWorkflowService();

export type { DigitizationWorkflowService };
